import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * SchemaPlusInjector, Adds additional schema types that complement
 * the existing SEOHead.jsx schema: HowTo, VideoObject, Quiz, and
 * AggregateRating layered on top of repair/sell pages.
 *
 * STRICTLY ADDITIVE. SEOHead.jsx already handles FAQPage, Service,
 * BreadcrumbList, WebPage, LocalBusiness, Speakable. This adds the rest.
 */

const REPAIR_HOWTO = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How to Get Your Phone Repaired at 2A Electronics',
  description: 'Step-by-step process for getting your phone or device repaired at 2A Electronics Chicago.',
  totalTime: 'PT30M',
  estimatedCost: { '@type': 'MonetaryAmount', currency: 'USD', value: '69' },
  supply: [{ '@type': 'HowToSupply', name: 'Your damaged device' }],
  tool: [
    { '@type': 'HowToTool', name: 'Genuine Apple parts (when applicable)' },
    { '@type': 'HowToTool', name: 'Professional repair toolkit' },
  ],
  step: [
    {
      '@type': 'HowToStep',
      position: 1,
      name: 'Get an Instant Estimate',
      text: 'Use our free online estimate tool or call us at (773) 766-5814 to get a same-day repair quote.',
      url: 'https://2aelectronics.com/repair-quote',
    },
    {
      '@type': 'HowToStep',
      position: 2,
      name: 'Drop Off or Mail In',
      text: 'Walk in to our Irving Park location, book an appointment, or ship your device with a FREE prepaid label.',
      url: 'https://2aelectronics.com/mail-in',
    },
    {
      '@type': 'HowToStep',
      position: 3,
      name: 'Same-Day Repair',
      text: 'Our certified technicians repair your device, most screens and batteries are done in 30-90 minutes.',
    },
    {
      '@type': 'HowToStep',
      position: 4,
      name: 'Pickup & Warranty',
      text: 'Pick up your repaired device or have it shipped back with a 1-month warranty on parts and labor.',
    },
  ],
};

const REPAIR_VIDEO = {
  '@context': 'https://schema.org',
  '@type': 'VideoObject',
  name: 'iPhone Screen Repair in Chicago | Behind the Scenes at 2A Electronics',
  description: 'Watch our certified technicians perform a same-day iPhone screen repair at 2A Electronics in Chicago.',
  thumbnailUrl: 'https://2aelectronics.com/og-image.jpg',
  uploadDate: '2026-01-01T08:00:00-06:00',
  duration: 'PT2M30S',
  contentUrl: 'https://2aelectronics.com/videos/iphone-screen-repair.mp4',
  embedUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
  publisher: {
    '@type': 'Organization',
    name: '2A Electronics',
    logo: { '@type': 'ImageObject', url: 'https://2aelectronics.com/logo512.png' },
  },
};

const AGG_RATING = {
  '@context': 'https://schema.org',
  '@type': 'AggregateRating',
  itemReviewed: { '@type': 'LocalBusiness', '@id': 'https://2aelectronics.com/#business' },
  ratingValue: '5.0',
  bestRating: '5',
  worstRating: '1',
  ratingCount: '470',
  reviewCount: '470',
};

const SchemaPlusInjector = () => {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    const isRepair = path.startsWith('/repair') || path.startsWith('/repairs') || path === '/';
    const isSell = path.startsWith('/sell');
    const isTool = path.startsWith('/tools') || path === '/symptom-checker' || path === '/repair-or-replace-calculator';

    const ensure = (id, data) => {
      let el = document.getElementById(id);
      if (!data) {
        if (el) el.remove();
        return;
      }
      if (!el) {
        el = document.createElement('script');
        el.id = id;
        el.type = 'application/ld+json';
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(data);
    };

    ensure('howto-schema', isRepair ? REPAIR_HOWTO : null);
    ensure('video-schema', isRepair || isTool ? REPAIR_VIDEO : null);
    ensure('aggregate-rating-schema', isRepair || isSell || isTool ? AGG_RATING : null);

    return () => {
      // do not strip on unmount, next page will overwrite
    };
  }, [location.pathname]);

  return null;
};

export default SchemaPlusInjector;
